import { InfoGatheringAPI } from './API'
import {
  GetChecklistRequest,
  ProvideSupportingItemBusinessInfoRequest,
  ProvideSupportingItemBusinessRefundMethodRequest,
  ProvideSupportingItemDependentInfoRequest,
  ProvideSupportingItemNotesRequest,
  ProvideSupportingItemPersonalInfoRequest,
  ProvideSupportingItemPersonalRefundMethodRequest,
  ProvideSupportingItemSpousalInfoRequest,
  RemoveSupportingItemDependentInfoRequest,
  RequestedSupportingItemUpdatedRealtimeEvent,
} from '@taxfyle/api-internal/internal/info_gathering_pb'
import { Id } from '../types/definitions'
import { collection } from '../infra/collection'
import Checklist, { DependentsInfoSupportingItemInput } from './Checklist'
import { ChecklistDTO } from './dtos/info-gathering'
import {
  mapChecklistProtoToDTO,
  mapSupportingItemToDto,
} from './dtos/checklistGrpcDtoMapper'
import { Observable } from 'rxjs'
import { filter } from 'rxjs/operators'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import TypeCase = RealtimeEvent.TypeCase
import { maybeParseDate } from '../utils/date-util'
import { PrimeDocument } from '../documents/DocumentStore'
import { PrimeDocumentTypeByShortId } from '../documents/DocumentTypeStore'

export default class InfoGatheringStore {
  /**
   * Checklist collection.
   */
  checklists = collection<ChecklistDTO, Checklist>({
    create: () => new Checklist(this.primeDocument, this.primeDocumentType),
    idAttribute: 'job_id',
  })

  constructor(
    private api: InfoGatheringAPI,
    private events$: Observable<RealtimeEvent>,
    private readonly primeDocument: PrimeDocument,
    private readonly primeDocumentType: PrimeDocumentTypeByShortId,
  ) {
    this.events$
      .pipe(
        filter((event) => {
          return (
            event.getTypeCase() ===
              TypeCase.REQUESTED_SUPPORTING_ITEM_UPDATED ||
            event.getTypeCase() === TypeCase.DOCUMENT_REQUEST_CREATED
          )
        }),
      )
      .subscribe((e) => this._onRemoteItemUpdated(e))
  }

  async getChecklist(jobId: Id) {
    const id = Id.assert(jobId)
    const request = new GetChecklistRequest().setJobId(id)
    return this.api
      .getChecklist(request)
      .then((response) => mapChecklistProtoToDTO(response, id))
      .then((checklist) => this.checklists.set(checklist))
  }

  async provideSupportingItemNotes({
    jobId,
    itemId,
    notes,
  }: {
    jobId: Id
    itemId: Id
    notes: string
  }) {
    const request = new ProvideSupportingItemNotesRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setNotes(notes)
    await this.api.provideSupportingItemNotes(request)
    await this.getChecklist(jobId)
  }

  async provideSupportingItemPersonalInfo({
    jobId,
    itemId,
    profileId,
    profileVersion,
  }: {
    jobId: Id
    itemId: Id
    profileId: Id
    profileVersion: number
  }) {
    const request = new ProvideSupportingItemPersonalInfoRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setProfileId(Id.assert(profileId))
      .setProfileVersion(profileVersion)
    await this.api.provideSupportingItemPersonalInfo(request)
    await this.getChecklist(jobId)
  }

  async provideSupportingItemSpousalInfo({
    jobId,
    itemId,
    profileId,
    profileVersion,
  }: {
    jobId: Id
    itemId: Id
    profileId: Id
    profileVersion: number
  }) {
    const request = new ProvideSupportingItemSpousalInfoRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setProfileId(Id.assert(profileId))
      .setProfileVersion(profileVersion)
    await this.api.provideSupportingItemSpousalInfo(request)
    await this.getChecklist(jobId)
  }

  async provideSupportingItemBusinessInfo({
    jobId,
    itemId,
    profileId,
    profileVersion,
  }: {
    jobId: Id
    itemId: Id
    profileId: Id
    profileVersion: number
  }) {
    const request = new ProvideSupportingItemBusinessInfoRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setProfileId(Id.assert(profileId))
      .setProfileVersion(profileVersion)
    await this.api.provideSupportingItemBusinessInfo(request)
    await this.getChecklist(jobId)
  }

  async provideSupportingItemPersonalRefundMethod({
    jobId,
    itemId,
    refundMethodId,
    refundMethodVersion,
  }: {
    jobId: Id
    itemId: Id
    refundMethodId: Id
    refundMethodVersion: number
  }) {
    const request = new ProvideSupportingItemPersonalRefundMethodRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setRefundMethodId(Id.assert(refundMethodId))
      .setRefundMethodVersion(refundMethodVersion)
    await this.api.provideSupportingItemPersonalRefundMethod(request)
    await this.getChecklist(jobId)
  }

  async provideSupportingItemBusinessRefundMethod({
    jobId,
    itemId,
    refundMethodId,
    refundMethodVersion,
  }: {
    jobId: Id
    itemId: Id
    refundMethodId: Id
    refundMethodVersion: number
  }) {
    const request = new ProvideSupportingItemBusinessRefundMethodRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setRefundMethodId(Id.assert(refundMethodId))
      .setRefundMethodVersion(refundMethodVersion)
    await this.api.provideSupportingItemBusinessRefundMethod(request)
    await this.getChecklist(jobId)
  }

  async provideSupportingItemDependentInfo({
    jobId,
    itemId,
    profileId,
    profileVersion,
  }: {
    jobId: Id
    itemId: Id
    profileId: Id
    profileVersion: number
  }) {
    const request = new ProvideSupportingItemDependentInfoRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setProfileId(Id.assert(profileId))
      .setProfileVersion(profileVersion)
    await this.api.provideSupportingItemDependentInfo(request)
    this._optimisticallyAddDependent(jobId, itemId, profileId, profileVersion)
    void this.getChecklist(jobId)
  }

  async removeSupportingItemDependentInfo({
    jobId,
    itemId,
    profileId,
  }: {
    jobId: Id
    itemId: Id
    profileId: Id
  }) {
    const request = new RemoveSupportingItemDependentInfoRequest()
      .setJobId(Id.assert(jobId))
      .setId(Id.assert(itemId))
      .setProfileId(Id.assert(profileId))
    await this.api.removeSupportingItemDependentInfo(request)
    this._optimisticallyRemoveDependent(jobId, itemId, profileId)
    void this.getChecklist(jobId)
  }

  private _getDependentsInfoInput(jobId: Id, itemId: Id) {
    const input = this.checklists
      .get(jobId)
      ?.getItemById(Id.assert(itemId))?.input
    return input instanceof DependentsInfoSupportingItemInput ? input : null
  }

  private _optimisticallyAddDependent(
    jobId: Id,
    itemId: Id,
    profileId: Id,
    profileVersion: number,
  ) {
    this._getDependentsInfoInput(jobId, itemId)?.addDependent({
      profileId: Id.assert(profileId),
      profileVersion,
    })
  }

  private _optimisticallyRemoveDependent(
    jobId: Id,
    itemId: Id,
    profileId: Id,
  ) {
    this._getDependentsInfoInput(jobId, itemId)?.removeDependent(
      Id.assert(profileId),
    )
  }

  /**
   * Maps the proto message to an event type.
   *
   * @param e
   * @private
   */
  private _onRemoteItemUpdated(e: RealtimeEvent) {
    switch (e.getTypeCase()) {
      case TypeCase.REQUESTED_SUPPORTING_ITEM_UPDATED:
        this.updateRequestedItem(e.getRequestedSupportingItemUpdated())
        break
      case TypeCase.DOCUMENT_REQUEST_CREATED: {
        const jobId = e.getDocumentRequestCreated()?.getJobId()
        if (jobId) {
          this.getChecklist(jobId)
        }
        break
      }
    }
  }

  /**
   * Updates the requested supporting item from an event.
   *
   * @param event
   * @returns
   */
  private updateRequestedItem(
    event?: RequestedSupportingItemUpdatedRealtimeEvent,
  ) {
    const jobId = event?.getJobId()
    const protoDTO = event?.getRequestedSupportingItem()
    if (!protoDTO) {
      return
    }
    const itemDto = mapSupportingItemToDto(protoDTO)

    const checklist = this.checklists.get(jobId)

    if (!checklist) {
      return
    }

    const item = checklist.getItemById(itemDto.id)

    if (!item) {
      return
    }

    // Only update if the server timestamp is more recent.
    const lastUpdated = maybeParseDate(itemDto.update_time)
    if (!lastUpdated) {
      return
    }
    if (!item.dateModified || item.dateModified < lastUpdated) {
      item.set(itemDto, {
        parse: true,
        stripUndefined: true,
      })
    }
  }
}
